import Experience from "../Experience"
import * as THREE from 'three'
import gsap from 'gsap'
// Vertex Shader
const fogVertexShader = `
  varying vec3 vWorldPosition;

  void main() {
      vec4 worldPosition = modelMatrix * vec4(position, 1.0);
      vWorldPosition = worldPosition.xyz;

      gl_Position = projectionMatrix * viewMatrix * worldPosition;
  }
`;

// Fragment Shader (3D Simplex Noise)
const fogFragmentShader = `
uniform float uTime;
uniform vec3  uColor;
uniform float uRadius;
uniform float uIntensity;
uniform vec3  uCenter;
uniform float uSplatterMultiplier;
uniform float uSplatterRadiusMultiplier;
uniform float uSplatterThreshold;
uniform float uSplatterMixAmount;
uniform bool  uInteractive;
uniform float uValues[5];


varying vec3  vWorldPosition;

#define STEPS 1
#define PI 3.14159265359
#define TWO_PI 6.28318530718
float hash(float n) {
  return fract(sin(n)*43758.5453123);
}
// We'll keep the snoise function for the fog deformation
float snoise(vec3 v) {
  // A more chaotic noise function
  vec3 p = floor(v);
  vec3 f = fract(v);
  
  f = f*f*(3.0-2.0*f); // Smooth interpolation
  
  float n = p.x + p.y*157.0 + 113.0*p.z;
  
  // Mix four corners
  return mix(
    mix(
      mix(hash(n+0.0), hash(n+1.0), f.x),
      mix(hash(n+157.0), hash(n+158.0), f.x),
      f.y),
    mix(
      mix(hash(n+113.0), hash(n+114.0), f.x),
      mix(hash(n+270.0), hash(n+271.0), f.x),
      f.y),
    f.z);
}

// Helper hash function for the noise


vec3 mod289(vec3 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 mod289(vec4 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec4 permute(vec4 x) {
  return mod289(((x * 34.0) + 1.0) * x);
}

vec4 taylorInvSqrt(vec4 r) {
  return 1.79284291400159 - 0.85373472095314 * r;
}

// Perlin noise function
float perlin(vec3 v) {
  const vec2 C = vec2(1.0/6.0, 1.0/3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);

  // First corner
  vec3 i  = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);

  // Other corners
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);

  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;

  // Permutations
  i = mod289(i);
  vec4 p = permute(permute(permute(
             i.z + vec4(0.0, i1.z, i2.z, 1.0))
           + i.y + vec4(0.0, i1.y, i2.y, 1.0))
           + i.x + vec4(0.0, i1.x, i2.x, 1.0));

  // Gradients
  float n_ = 0.142857142857; // 1/7
  vec3  ns = n_ * D.wyz - D.xzx;

  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);

  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);

  vec4 x = x_ * ns.x + ns.yyyy;
  vec4 y = y_ * ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);

  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);

  vec4 s0 = floor(b0)*2.0 + 1.0;
  vec4 s1 = floor(b1)*2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));

  vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;

  vec3 p0 = vec3(a0.xy,h.x);
  vec3 p1 = vec3(a0.zw,h.y);
  vec3 p2 = vec3(a1.xy,h.z);
  vec3 p3 = vec3(a1.zw,h.w);

  // Normalise gradients
  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2,p2), dot(p3,p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;

  // Mix final noise value
  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}
// Helper function to process a single fog
void processFog(vec3 p, vec3 rayDir, float stepSize, vec3 fogCenter, float fogRadius, inout vec3 accumColor, inout float accumAlpha, bool isInteractive) {
  for (int i = 0; i < STEPS; i++) {

    //---------------------------------------
    // 2) Local position relative to fog center
    //---------------------------------------
    vec3 localP = p - fogCenter;
    float angle = atan(localP.z, localP.x);

    // Distance from center (in XZ plane)
    vec2 dir = vec2(localP.x, localP.z);
    float distanceFromCenter = length(dir) / fogRadius;

    // Basic 2D "UV" for noise
    vec2 noiseUV = vec2(p.x * 0.8, p.z * 0.8);

    //---------------------------------------
    // 3) Main shape / deformation
    //---------------------------------------
    float timeFactor = uTime;
    float freq = 0.5;
    
    // Scale down noise effects for non-interactive mode
    float noiseScale = isInteractive ? 1.0 : 2.0;
    float timeScale = isInteractive ? 1.0 : 1.2;
    float deformationScale = isInteractive ? 1.0 : 0.2;
    float baseShape = perlin(vec3(localP)) * 0.5 * noiseScale;

    // Using simplex noise for the main fog deformation
    float noise1 = snoise(vec3(p * 50.0)) * 0.5 * noiseScale;
    float noise2 = snoise(vec3(p * 50.0)) * 0.5 * noiseScale;

    // Fade out deformations near center
    float centerBlend = smoothstep(0.0, 0.5, distanceFromCenter*1.0);
    float deformation = 
      mix(
        0.0, 
        baseShape+noise1+noise2, 
        centerBlend
      );

    // Subtle additional movement - reduced for non-interactive mode
    vec2 worldDir = p.xz;
    deformation += perlin(vec3(worldDir * 0.5, timeFactor * timeScale)) * 0.75 * centerBlend * noiseScale * deformationScale;

    // "Wobbly" radius - less wobbly for non-interactive mode
    float wobbleScale = isInteractive ? 0.5 : 0.5;
    float wobblyRadius = fogRadius * (0.8 + deformation * wobbleScale);
    float r = length(localP);

    // Edge region: define "shell" near wobblyRadius
    float edgeStart = wobblyRadius * 0.8; 
    float edgeEnd   = wobblyRadius * 0.9;  
    float mask = 1.0 - step(edgeStart, r);

    // Base alpha from the volumetric pass
    float alpha = 0.3 * mask;

    float splatterStart =  fogRadius * uSplatterRadiusMultiplier;  
    float splatterEnd = splatterStart - 0.9;
    // 0 if r < splatterStart, 1 if r > splatterEnd
    float outsideMask = smoothstep(splatterStart, splatterEnd, r);

    vec3 splatterUV = vec3(localP.xyz * uSplatterMultiplier); 
    float splatterNoise = perlin(vec3(splatterUV));

    float splatterThreshold = uSplatterThreshold;
    float splatterMask = step(splatterThreshold, splatterNoise) * outsideMask;

    // Add a bit more alpha for the splatter
    float splatterAlpha = min(1.0 - alpha,1.5 * splatterMask); 
    if (alpha == 0.0) {
      alpha += min(splatterAlpha, 0.3);
    }
    // Mix splatter color
    float splatterMixAmount = uSplatterMixAmount * splatterMask;
    vec3 finalColor = mix(uColor, uColor, splatterMixAmount);

    // Alpha compositing
    float outAlpha = alpha * (1.0 - accumAlpha);
    accumColor += alpha * finalColor;
    accumAlpha += outAlpha;

    // Advance to next sample position
    p += rayDir * stepSize;
  }
}

void main() {
  //---------------------------------------
  // 1) Setup basic volumetric raymarch
  //---------------------------------------
  vec3 rayDir = normalize(vWorldPosition - cameraPosition);

  // Accumulate color and alpha
  vec3 accumColor = vec3(0.0);
  float accumAlpha = 0.0;
  
  if (uInteractive) {
    // Interactive mode: single fog at uCenter
    // Distance checks to figure out how far we travel
    float distToCenter = length(cameraPosition - vec3(0.0,0.0,0.0));
    float hitDist = distToCenter - uRadius;
    float tNear = (hitDist > 0.0) ? hitDist : 0.0;
    float tFar  = distToCenter + uRadius;

    // Step size for volumetric sampling
    float stepSize = (tFar - tNear) / float(STEPS);

    // Start point on the ray
    vec3 p = cameraPosition + rayDir * tNear;
    
    // Process the single fog
    processFog(p, rayDir, stepSize, uCenter, uRadius, accumColor, accumAlpha, true);
  } else {
    // Non-interactive mode: multiple fogs based on uValues
    // Define fog positions based on axes directions
    vec3 axisDirections[5];
    axisDirections[0] = vec3(1.0, 0.0, 0.0);  // east
    axisDirections[1] = vec3(-1.0, 0.0, 0.0); // west
    axisDirections[2] = vec3(0.0, 1.0, 0.0);  // north
    axisDirections[3] = vec3(0.0, -1.0, 0.0); // south
    axisDirections[4] = vec3(0.0, 0.0, 0.0);  // center/umami
    
    vec3 baseCenter = vec3(0.0, 4.0, 0.0); // Base center position
    
    // Process each fog
    for (int i = 0; i < 5; i++) {
      // Skip if value is too small
      if (uValues[i] < 0.1) continue;
      
      // Calculate fog position and scaled radius
      vec3 fogCenter = baseCenter + axisDirections[i] * uValues[i];
      float fogRadius = uRadius * (0.3 + uValues[i] * 0.1); // Scale radius based on value
      
      // Calculate distance for this fog
      float distToFog = length(cameraPosition - fogCenter);
      float hitDist = distToFog - fogRadius;
      float tNear = (hitDist > 0.0) ? hitDist : 0.0;
      float tFar = distToFog + fogRadius;
      
      // Step size for this fog
      float stepSize = (tFar - tNear) / float(STEPS);
      
      // Start point on the ray for this fog
      vec3 p = cameraPosition + rayDir * tNear;
      
      // Process this fog
      processFog(p, rayDir, stepSize, fogCenter, fogRadius, accumColor, accumAlpha, false);
    }
  }



  // Final output
  gl_FragColor = vec4(accumColor, accumAlpha * uIntensity);
}
`;


export default class Fog {
  /**
   * Constructor
   * @param {number} radius - The radius of the fog sphere.
   */
  constructor(radius, axes) {
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.radius = radius;
    this.axes = axes;
    this.interactive = this.experience.interactive;

    // Create the ShaderMaterial
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new THREE.Color('#f40c6a') },
        uRadius: { value: 1.26 },
        uCenter: { value: new THREE.Vector3(0, 0, 0) },
        uIntensity: { value: 0.0 },
        uSplatterMultiplier: { value: 7.65 },
        uSplatterRadiusMultiplier: { value: 1.11 },
        uSplatterThreshold: { value: 0.78 },
        uSplatterMixAmount: { value: 0.0 },
        uInteractive: { value: this.interactive },
        uValues: { value: [0.0, 0.0, 0.0, 0.0, 0.0] },
      },
      vertexShader: fogVertexShader,
      fragmentShader: fogFragmentShader,
      transparent: false,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide
    });

    this.animating = false

    this.debug = this.experience.debug
    if (this.debug.active) {
      this.debugFolder = this.debug.ui.addFolder('Fog')
      this.debugFolder.add(this.material.uniforms.uIntensity, 'value').min(0).max(3).step(0.01).name('Intensity')
      this.debugFolder.add(this.material.uniforms.uRadius, 'value').min(0).max(3).step(0.01).name('Radius')
      this.debugFolder.add(this.material.uniforms.uSplatterMultiplier, 'value').min(0).max(20).step(0.01).name('Splatter Multiplier')
      this.debugFolder.add(this.material.uniforms.uSplatterRadiusMultiplier, 'value').min(0).max(3).step(0.01).name('Splatter Radius Multiplier')
      this.debugFolder.add(this.material.uniforms.uSplatterThreshold, 'value').min(0).max(3).step(0.01).name('Splatter Threshold')
      this.debugFolder.add(this.material.uniforms.uSplatterMixAmount, 'value').min(0).max(3).step(0.01).name('Splatter Mix Amount')
    }

    const geometry = new THREE.SphereGeometry(
      this.radius * 0.5,
      100,
      100
    );

    this.sphere = new THREE.Mesh(geometry, this.material);

    this.sphere.position.set(-0.033, 4 * 1.01, 0);
    this.sphere.renderOrder = 1;

    this.scene.add(this.sphere);



  }
  show() {
    this.animateIntensity(3, 2.03, { delay: 1 })
  }
  animateIntensity(duration, intensity, { delay = 0 }) {
    gsap.to(this.material.uniforms.uIntensity, {
      duration: duration,
      value: intensity,
      ease: 'power4.inOut',
      delay: delay
    });
  }
  setPosition(x, y, z) {
    this.material.uniforms.uCenter.value.set(x, y, z);
  }
  animateToPosition(duration, position) {
    if (this.animating) return;
    this.animating = true;
    const initial = {
      center: this.material.uniforms.uCenter.value,
    }
    gsap.to(initial.center, {
      duration: duration,
      y: position.y,
      x: position.x,
      z: position.z,
      onUpdate: () => {
      },
      onComplete: () => {
        this.animating = false;
      }
    },
    );
  }
  invertPositionToAxes(position) {
    // Suppose our anchor center is (0,4,0)
    const center = new THREE.Vector3(0, 4, 0);
    const offset = position.clone().sub(center);
    const x = offset.x;
    const y = offset.y;

    // Calculate distance from center
    const distance = offset.length();

    // Calculate directional values proportionally from 0 to 3 up to 3 units
    // For each direction, we'll use a simple linear scale clamped at max 3
    let east = 0, west = 0, north = 0, south = 0;

    // East-West axis
    if (x > 0) {
      // Positive x goes east
      east = Math.min(x, 3); // Linear scale clamped at 3
    } else {
      // Negative x goes west
      west = Math.min(-x, 3); // Convert to positive and clamp at 3
    }

    // North-South axis
    if (y > 0) {
      // Positive y goes north
      north = Math.min(y, 3); // Linear scale clamped at 3
    } else {
      // Negative y goes south
      south = Math.min(-y, 3); // Convert to positive and clamp at 3
    }

    // Only return a center value if within 1 unit of center, proportionally
    let centerValue = 0;
    if (distance <= 1) {
      centerValue = (1 - distance) * 3; // Proportional to how close it is to center
    }

    // Return an object mapping to your axes:
    return {
      east,
      west,
      north,
      south,
      center: centerValue
    };
  }
  hoverFog(intersectPoint) {
    if (!intersectPoint) return;
    if (this.animating) return;
    // Create a GSAP animation that lerps to the target position
    gsap.to(this.material.uniforms.uCenter.value, {
      duration: 0.75, // Adjust duration for faster/slower lerping
      x: intersectPoint.x,
      y: intersectPoint.y,
      z: intersectPoint.z,
      ease: "power2.out" // Smooth easing function
    });
  }
  updateValues(values) {
    const center = new THREE.Vector3(0, 4, 0)

    let totalOffset = new THREE.Vector3(0, 0, 0)

    let valuesArray = []

    values.forEach(v => {
      if (v.name === 'center') {
        valuesArray.push(v.value)
        return
      }
      const axis = this.axes.axesData.find(a => a.name === v.name)
      if (!axis) return
      totalOffset.add(axis.direction.clone().multiplyScalar(v.value))
      valuesArray.push(v.value)
    })

    const umamiValue = values.find(v => v.name === 'center')?.value || 0
    const umamiFactor = umamiValue / 3 // range 0..1

    // "Lerp" from totalOffset to (0,0,0) by umamiFactor, i.e. pulling it toward center
    // If umamiFactor=0 => no pull, 
    // If umamiFactor=1 => fully center.
    totalOffset.lerp(new THREE.Vector3(0, 0, 0), umamiFactor)

    if (this.interactive) {
      const finalPosition = center.clone().add(totalOffset)
      this.animateToPosition(1, finalPosition)
    } else {
      this.animateFogs(1, valuesArray)
    }
  }
  animateFogs(duration, valuesArray) {
    // Ensure the array has exactly 5 values
    const safeValues = valuesArray.slice(0, 5);
    while (safeValues.length < 5) {
      safeValues.push(0.0);
    }
    for (let i = 0; i < safeValues.length; i++) {
      const initial = {
        value: this.material.uniforms.uValues.value[i],
      }
      gsap.to(initial, {
        duration: duration,
        value: safeValues[i],
        ease: 'power4.inOut',
        onUpdate: () => {
          this.material.uniforms.uValues.value[i] = initial.value
        }
      })
    }
  }

  // For console
  // window.experience.world.luxurySphere.fog.updateValues([{name: 'east', value: 1}

  /**
   * Dynamically updates the sphere's radius in the shader.

   * If you want to truly resize the geometry, you may re-generate geometry.

   */
  setRadius(newRadius) {
    this.material.uniforms.uRadius.value = newRadius * 0.5;
  }
  addTime(increment) {
    this.material.uniforms.uTime.value += increment
  }

  /**
   * Call on each frame to animate the noise over time.
   */
  update() {
    // Slower time progression for non-interactive mode
    const timeIncrement = this.interactive ? 0.01 : 0.005;
    this.addTime(timeIncrement);
  }
}
